import { BRAND_SITE_NAME, BRAND_PERSON_NAME, BRAND_HANDLE, BRAND_SITE_URL, BRAND_AGE } from "@/lib/brandConfig";
export type Language = string; // Now supports regional codes like "es-AR", "en-US", etc.

// Extract base language code from regional code (e.g., "es-AR" -> "es")
export const getBaseLanguage = (regionalCode: string | undefined): "es" | "en" => {
  if (!regionalCode) return "es"; // Default to Spanish
  const baseCode = regionalCode.split("-")[0].toLowerCase();
  return baseCode === "en" ? "en" : "es";
};

export const getBrowserLanguage = (region?: "LATAM" | "US" | "OTHER"): Language => {
  if (typeof window === "undefined") return "es-US";
  
  // Get browser language with regional code
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  // If region is LATAM, use Spanish with detected country
  if (region === "LATAM") {
    // Try to get country from browser language
    const parts = browserLang.split("-");
    if (parts[0].toLowerCase() === "es" && parts[1]) {
      return browserLang; // Keep the regional code like "es-AR", "es-MX"
    }
    return "es-US"; // Default Spanish
  }
  
  // If region is US, use English US
  if (region === "US") {
    return "en-US";
  }
  
  // For OTHER or no region, return the browser's language code
  return browserLang || "en-US";
};

export const translations = {
  es: {
    common: {
      siteName: BRAND_SITE_NAME,
      tagline: "Modelo y creadora para redes",
      description:
        `${BRAND_PERSON_NAME} — modelo y creadora de contenido para redes sociales, ${BRAND_AGE} años, de Buenos Aires, Capital Federal (CABA). Campañas, colaboraciones y comunidad.`,
      keywords:
        `${BRAND_PERSON_NAME}, modelo, creadora de contenido, redes sociales, Buenos Aires, CABA, Capital Federal, influencer, ${BRAND_AGE} años, Instagram, TikTok`,
      ogDescription: `Modelo · Creadora para redes · ${BRAND_AGE} años · Buenos Aires (CABA)`,
    },
    pages: {
      index: {
        title: `${BRAND_PERSON_NAME} | Modelo y creadora de contenido — Buenos Aires (CABA)`,
        description:
          `${BRAND_PERSON_NAME} — modelo y creadora de contenido para redes sociales, ${BRAND_AGE} años, Buenos Aires, Capital Federal (CABA). Campañas, colaboraciones y comunidad.`,
      },
      contentCreator: {
        title: `${BRAND_PERSON_NAME} - Content Creator | Lifestyle & Beauty Digital Creator`,
        description: `${BRAND_PERSON_NAME} es una joven creadora de contenido, modelo, deportista y estudiante de nutrición. Le encanta el lifestyle y comparte contenido auténtico en Instagram, TikTok y YouTube. Sigue a @${BRAND_HANDLE}.`,
      },
      creadoraContenido: {
        title: `${BRAND_PERSON_NAME} - Creadora de Contenido Digital | Lifestyle & Beauty`,
        description: `${BRAND_PERSON_NAME} es una joven creadora de contenido, modelo, deportista y estudiante de nutrición. Le encanta el lifestyle y comparte contenido auténtico en Instagram, TikTok y YouTube.`,
      },
      influencer: {
        title: `${BRAND_PERSON_NAME} - Influencer Argentina | Lifestyle, Belleza & Viajes`,
        description: `${BRAND_PERSON_NAME} es una joven creadora de contenido, modelo, deportista y estudiante de nutrición. Le encanta el lifestyle y comparte contenido auténtico sobre bienestar, belleza y vida saludable.`,
      },
      fotos: {
        title: `Fotos de ${BRAND_PERSON_NAME} | Galería de Fotos & Imágenes`,
        description: `Galería de fotos de ${BRAND_PERSON_NAME}. Descubre imágenes exclusivas en Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      imagenes: {
        title: `Imágenes de ${BRAND_PERSON_NAME} | Fotos & Galería`,
        description: `Imágenes y fotos de ${BRAND_PERSON_NAME}. Galería completa de contenido visual en Instagram, TikTok y YouTube.`,
      },
      reels: {
        title: `Reels de ${BRAND_PERSON_NAME} | Videos Cortos de Instagram & TikTok`,
        description: `Reels y videos cortos de ${BRAND_PERSON_NAME}. Contenido viral en Instagram y TikTok. Sigue a @${BRAND_HANDLE} para más reels.`,
      },
      videos: {
        title: `Videos de ${BRAND_PERSON_NAME} | Contenido en YouTube, TikTok e Instagram`,
        description: `Videos de ${BRAND_PERSON_NAME} en YouTube, TikTok e Instagram. Contenido exclusivo y entretenido. @${BRAND_HANDLE}`,
      },
      video: {
        title: `Video de ${BRAND_PERSON_NAME} | Contenido Exclusivo`,
        description: `Video exclusivo de ${BRAND_PERSON_NAME}. Contenido en YouTube, TikTok e Instagram. @${BRAND_HANDLE}`,
      },
      lifestyle: {
        title: `Lifestyle con ${BRAND_PERSON_NAME} | Consejos & Inspiración`,
        description: `Descubre contenido lifestyle con ${BRAND_PERSON_NAME}. Rutinas diarias, tips de bienestar, moda y momentos cotidianos que inspiran.`,
      },
      nutricion: {
        title: `Nutrición & Bienestar con ${BRAND_PERSON_NAME} | Consejos Saludables`,
        description: `${BRAND_PERSON_NAME} es estudiante de nutrición y comparte consejos de nutrición y bienestar. Recetas saludables, tips nutricionales y estilo de vida saludable basados en su formación académica.`,
      },
      belleza: {
        title: `Belleza & Beauty Tips con ${BRAND_PERSON_NAME} | Consejos de Belleza`,
        description: `Consejos de belleza y rutinas con ${BRAND_PERSON_NAME}. Tips de maquillaje, skincare y cuidado personal.`,
      },
      viajes: {
        title: `Viajes & Travel con ${BRAND_PERSON_NAME} | Destinos e Inspiración`,
        description: `Descubre los viajes de ${BRAND_PERSON_NAME}. Destinos, experiencias y recomendaciones de viaje.`,
      },
      tiktok: {
        title: `${BRAND_PERSON_NAME} en TikTok | Videos Virales & Contenido Trending`,
        description: `Sigue a ${BRAND_PERSON_NAME} en TikTok. Videos virales, contenido trending y entretenimiento. @${BRAND_HANDLE}`,
      },
      instagram: {
        title: `${BRAND_PERSON_NAME} Instagram | Reels, Stories & Contenido Diario`,
        description: `Sigue a ${BRAND_PERSON_NAME} en Instagram. Reels, stories y contenido diario. @${BRAND_HANDLE}`,
      },
      youtube: {
        title: `${BRAND_PERSON_NAME} YouTube | Videos, Vlogs & Contenido Exclusivo`,
        description: `Canal de YouTube de ${BRAND_PERSON_NAME}. Videos, vlogs y contenido exclusivo. Suscríbete a @${BRAND_HANDLE}`,
      },
      erome: {
        title: `${BRAND_PERSON_NAME} - Erome | Videos & Contenido Exclusivo`,
        description: `${BRAND_PERSON_NAME} en Erome. Videos y contenido exclusivo.`,
      },
      filtrados: {
        title: `${BRAND_PERSON_NAME} - Contenido Oficial | Instagram, TikTok y YouTube`,
        description: `Contenido oficial de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      filtro: {
        title: `${BRAND_PERSON_NAME} - Contenido | Instagram, TikTok y YouTube`,
        description: `Contenido de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      leaked: {
        title: `${BRAND_PERSON_NAME} - Contenido Auténtico | Instagram, TikTok y YouTube`,
        description: `Contenido auténtico de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      onlyfans: {
        title: `${BRAND_PERSON_NAME} - Onlyfans | Contenido Oficial`,
        description: `${BRAND_PERSON_NAME} en Onlyfans. Contenido oficial y exclusivo.`,
      },
      pack: {
        title: `${BRAND_PERSON_NAME} - Contenido Digital | Instagram, TikTok y YouTube`,
        description: `${BRAND_PERSON_NAME} contenido digital. Encuentra videos, fotos y reels oficiales en Instagram, TikTok y YouTube @${BRAND_HANDLE}.`,
      },
      desnuda: {
        title: `${BRAND_PERSON_NAME} - Contenido de Lifestyle | Instagram, TikTok y YouTube`,
        description: `Contenido de lifestyle de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      nude: {
        title: `${BRAND_PERSON_NAME} - Contenido Oficial | Instagram, TikTok y YouTube`,
        description: `Contenido oficial de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      premium: {
        title: `${BRAND_PERSON_NAME} - Contenido Premium | Instagram, TikTok y YouTube`,
        description: `Contenido premium de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      premiumInvite: {
        title: `Membresía VIP | ${BRAND_PERSON_NAME} — chat privado y contenido exclusivo`,
        description: `Suscribite al VIP con checkout seguro y activación rápida. También podés entrar solo al grupo de Telegram. @${BRAND_HANDLE}`,
      },
      premiumInviteSuccess: {
        title: `¡Gracias! | ${BRAND_PERSON_NAME}`,
        description: `Tu pago se procesó. Seguí los pasos para acceder a la comunidad y beneficios de ${BRAND_PERSON_NAME}. @${BRAND_HANDLE}`,
      },
      privado: {
        title: `${BRAND_PERSON_NAME} - Contenido Privado | Instagram, TikTok y YouTube`,
        description: `Contenido privado de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      contenido: {
        title: `${BRAND_PERSON_NAME} - Contenido | Instagram, TikTok y YouTube`,
        description: `Contenido de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      exclusivo: {
        title: `${BRAND_PERSON_NAME} - Contenido Exclusivo | Instagram, TikTok y YouTube`,
        description: `Contenido exclusivo de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      gratis: {
        title: `${BRAND_PERSON_NAME} - Contenido Gratis | Instagram, TikTok y YouTube`,
        description: `Contenido gratis de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      galeria: {
        title: `${BRAND_PERSON_NAME} - Galería | Fotos y Videos`,
        description: `Galería de ${BRAND_PERSON_NAME}. Fotos y videos exclusivos. @${BRAND_HANDLE}`,
      },
      portfolio: {
        title: `${BRAND_PERSON_NAME} - Portfolio | Producciones de Moda`,
        description: `Portfolio de ${BRAND_PERSON_NAME}. Producciones de moda y editoriales. @${BRAND_HANDLE}`,
      },
      mediakit: {
        title: `${BRAND_PERSON_NAME} - Media Kit | Colaboraciones`,
        description: `Media kit comercial de ${BRAND_PERSON_NAME}. Métricas, audiencia y conceptos de campaña. @${BRAND_HANDLE}`,
      },
      fotosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Fotos | Instagram, TikTok y YouTube`,
        description: `Fotos de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      videosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok y YouTube`,
        description: `Videos de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      pornhub: {
        title: `${BRAND_PERSON_NAME} - Contenido Oficial | Instagram, TikTok y YouTube`,
        description: `Contenido oficial de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      xvideos: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok y YouTube`,
        description: `Videos de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      fotosGratis: {
        title: `Fotos Gratis de ${BRAND_PERSON_NAME} | Galería Gratuita`,
        description: `Fotos gratis de ${BRAND_PERSON_NAME}. Galería gratuita de imágenes. @${BRAND_HANDLE}`,
      },
      videosGratis: {
        title: `Videos Gratis de ${BRAND_PERSON_NAME} | Contenido Gratuito`,
        description: `Videos gratis de ${BRAND_PERSON_NAME}. Contenido gratuito. @${BRAND_HANDLE}`,
      },
      tiktoker: {
        title: `${BRAND_PERSON_NAME} TikToker | Creadora de Contenido Viral Argentina`,
        description: `${BRAND_PERSON_NAME} TikToker argentina. Contenido viral, trends y entretenimiento. Sigue a @${BRAND_HANDLE} para videos diarios y challenges.`,
      },
      modelo: {
        title: `${BRAND_PERSON_NAME} Modelo | Modelo Argentina de Instagram y Redes Sociales`,
        description: `${BRAND_PERSON_NAME} modelo profesional argentina. Sesiones fotográficas, campañas publicitarias y contenido de moda. @${BRAND_HANDLE}`,
      },
      photography: {
        title: `${BRAND_PERSON_NAME} Photography | Contenido Visual y Fotográfico`,
        description: `Fotografía de ${BRAND_PERSON_NAME}. Contenido visual profesional, sesiones fotográficas y arte digital. @${BRAND_HANDLE}`,
      },
      artista: {
        title: `${BRAND_PERSON_NAME} Artista | Creativa Digital y Artista Visual`,
        description: `${BRAND_PERSON_NAME} artista digital. Contenido creativo, arte visual y expresión artística a través de redes sociales. @${BRAND_HANDLE}`,
      },
      fashion: {
        title: `${BRAND_PERSON_NAME} Fashion | Moda, Style y Tendencias`,
        description: `${BRAND_PERSON_NAME} moda y style. Tendencias, outfits, looks y consejos de moda. Inspiración fashion diaria. @${BRAND_HANDLE}`,
      },
      wellness: {
        title: `${BRAND_PERSON_NAME} Wellness | Bienestar, Salud y Vida Saludable`,
        description: `${BRAND_PERSON_NAME} wellness y bienestar. Consejos de salud, vida saludable, mindfulness y cuidado personal. @${BRAND_HANDLE}`,
      },
      digitalCreator: {
        title: `${BRAND_PERSON_NAME} Digital Creator | Creadora de Contenido Digital`,
        description: `${BRAND_PERSON_NAME} creadora digital. Contenido multiplataforma en Instagram, TikTok y YouTube. Innovación y creatividad digital. @${BRAND_HANDLE}`,
      },
      socialMedia: {
        title: `${BRAND_PERSON_NAME} Social Media | Redes Sociales y Contenido Multiplataforma`,
        description: `${BRAND_PERSON_NAME} en redes sociales. Contenido multiplataforma, estrategia digital y community building. @${BRAND_HANDLE}`,
      },
      personalidad: {
        title: `${BRAND_PERSON_NAME} Personalidad | Personalidad Digital Argentina`,
        description: `${BRAND_PERSON_NAME} personalidad digital. Contenido auténtico, experiencias personales y conexión genuina con la audiencia. @${BRAND_HANDLE}`,
      },
      beautyExpert: {
        title: `${BRAND_PERSON_NAME} Beauty Expert | Experta en Belleza y Maquillaje`,
        description: `${BRAND_PERSON_NAME} experta en belleza. Tips de maquillaje, skincare, rutinas de belleza y consejos de cuidado personal. @${BRAND_HANDLE}`,
      },
      travelInfluencer: {
        title: `${BRAND_PERSON_NAME} Travel Influencer | Viajes y Aventuras`,
        description: `${BRAND_PERSON_NAME} travel influencer. Aventuras de viaje, destinos únicos, experiencias culturales y exploración. @${BRAND_HANDLE}`,
      },
      creative: {
        title: `${BRAND_PERSON_NAME} Creative | Contenido Creativo y Artístico`,
        description: `${BRAND_PERSON_NAME} contenido creativo. Arte digital, fotografía conceptual, proyectos creativos y expresión artística. @${BRAND_HANDLE}`,
      },
      argentina: {
        title: `${BRAND_PERSON_NAME} Argentina | Modelo y creadora para redes`,
        description:
          `${BRAND_PERSON_NAME} — modelo y creadora de contenido para redes, ${BRAND_AGE} años, Buenos Aires (CABA). @${BRAND_HANDLE}`,
      },
      buenosAires: {
        title: `${BRAND_PERSON_NAME} Buenos Aires | Modelo y creadora para redes`,
        description:
          `${BRAND_PERSON_NAME} desde Buenos Aires, Capital Federal (CABA), ${BRAND_AGE} años. Modelo y creadora de contenido para redes sociales. @${BRAND_HANDLE}`,
      },
      deportista: {
        title: `${BRAND_PERSON_NAME} Deportista | Atleta y Contenido Fitness`,
        description: `${BRAND_PERSON_NAME} deportista. Rutinas de entrenamiento, nutrición deportiva y tips de bienestar físico. Contenido de atleta. @${BRAND_HANDLE}`,
      },
      fitness: {
        title: `${BRAND_PERSON_NAME} Fitness | Rutinas y Tips de Entrenamiento`,
        description: `${BRAND_PERSON_NAME} fitness. Workouts, rutinas de ejercicio y motivación. Combina entrenamiento con nutrición como deportista. @${BRAND_HANDLE}`,
      },
      entrenamiento: {
        title: `${BRAND_PERSON_NAME} Entrenamiento | Rutinas y Ejercicios`,
        description: `Rutinas de entrenamiento de ${BRAND_PERSON_NAME}. Ejercicios efectivos, tips de técnica y recuperación. Contenido deportivo. @${BRAND_HANDLE}`,
      },
      atleta: {
        title: `${BRAND_PERSON_NAME} Atleta | Deportista y Contenido Deportivo`,
        description: `${BRAND_PERSON_NAME} atleta. Disciplina, entrenamiento y superación personal. Contenido deportivo auténtico. @${BRAND_HANDLE}`,
      },
      sobreMi: {
        title: `Sobre ${BRAND_PERSON_NAME} | Modelo y creadora para redes`,
        description:
          `Conocé a ${BRAND_PERSON_NAME}: modelo y creadora de contenido para redes, ${BRAND_AGE} años, Buenos Aires (CABA). @${BRAND_HANDLE}`,
      },
      biografia: {
        title: `Biografía de ${BRAND_PERSON_NAME} | Content Creator Argentina`,
        description: `Biografía de ${BRAND_PERSON_NAME}. Creadora de contenido argentina en Instagram, TikTok y YouTube. Lifestyle, fitness y belleza. @${BRAND_HANDLE}`,
      },
      rutina: {
        title: `Rutina de ${BRAND_PERSON_NAME} | Rutina Diaria y Tips`,
        description: `Rutina diaria de ${BRAND_PERSON_NAME}. Mañanas productivas, entrenamiento, skincare y balance. Tips de lifestyle. @${BRAND_HANDLE}`,
      },
      recetas: {
        title: `Recetas de ${BRAND_PERSON_NAME} | Recetas Saludables`,
        description: `Recetas saludables de ${BRAND_PERSON_NAME}. Como estudiante de nutrición comparte smoothies, comidas equilibradas y snacks. @${BRAND_HANDLE}`,
      },
      skincare: {
        title: `Skincare ${BRAND_PERSON_NAME} | Cuidado de Piel y Belleza`,
        description: `Skincare de ${BRAND_PERSON_NAME}. Rutinas faciales, productos favoritos y tips de cuidado de piel. Belleza y wellness. @${BRAND_HANDLE}`,
      },
      colombia: {
        title: `Influencer Colombia | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, modelo y creadora de contenido con comunidad en Colombia. Lifestyle, fitness y belleza desde Buenos Aires para toda Latinoamérica. @${BRAND_HANDLE}`,
      },
      medellin: {
        title: `Influencer Medellín | ${BRAND_PERSON_NAME} — Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, influencer con audiencia en Medellín y Colombia. Modelo y creadora de contenido de lifestyle, fitness y belleza. @${BRAND_HANDLE}`,
      },
      bogota: {
        title: `Influencer Bogotá | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, influencer con seguidores en Bogotá y toda Colombia. Contenido auténtico de lifestyle, belleza y fitness. @${BRAND_HANDLE}`,
      },
      mexico: {
        title: `Influencer México | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, modelo y creadora de contenido con comunidad en México. Lifestyle, belleza y fitness con estilo latinoamericano. @${BRAND_HANDLE}`,
      },
      guadalajara: {
        title: `Influencer Guadalajara | ${BRAND_PERSON_NAME} — Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, influencer con audiencia en Guadalajara y la República Mexicana. Contenido de lifestyle, fitness y belleza auténtico. @${BRAND_HANDLE}`,
      },
      ciudadDeMexico: {
        title: `Influencer CDMX | ${BRAND_PERSON_NAME} — Ciudad de México`,
        description: `${BRAND_PERSON_NAME}, influencer con seguidores en Ciudad de México (CDMX). Modelo y creadora de contenido latinoamericana de lifestyle y belleza. @${BRAND_HANDLE}`,
      },
      chile: {
        title: `Influencer Chile | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, modelo y creadora de contenido con comunidad en Chile. Lifestyle, fitness y belleza desde Buenos Aires para toda Latinoamérica. @${BRAND_HANDLE}`,
      },
      santiago: {
        title: `Influencer Santiago | ${BRAND_PERSON_NAME} — Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, influencer con audiencia en Santiago de Chile. Modelo y creadora de contenido de lifestyle, fitness y belleza. @${BRAND_HANDLE}`,
      },
      espana: {
        title: `Influencer España | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, modelo y creadora de contenido con seguidores en España. Lifestyle, belleza y fitness con estilo latinoamericano. @${BRAND_HANDLE}`,
      },
      madrid: {
        title: `Influencer Madrid | ${BRAND_PERSON_NAME} — Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, influencer con comunidad en Madrid. Modelo y creadora de contenido de lifestyle, belleza y fitness. @${BRAND_HANDLE}`,
      },
      barcelona: {
        title: `Influencer Barcelona | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `${BRAND_PERSON_NAME}, influencer con audiencia en Barcelona. Contenido auténtico de lifestyle, moda y belleza para toda la comunidad hispanohablante. @${BRAND_HANDLE}`,
      },
      contacto: {
        title: `Contacto | ${BRAND_PERSON_NAME} — Modelo y Creadora de Contenido`,
        description: `Escribile a ${BRAND_PERSON_NAME}. Colaboraciones, campañas y consultas de marca. @${BRAND_HANDLE}`,
      },
      latinaInfluencer: {
        title: `Latina Influencer | ${BRAND_PERSON_NAME} — Creadora de Contenido Bilingüe`,
        description: `${BRAND_PERSON_NAME}, latina influencer argentina bilingüe (ES/EN). Modelo y creadora de lifestyle, fitness y belleza con alcance en USA y Latinoamérica. @${BRAND_HANDLE}`,
      },
      usa: {
        title: `Influencer USA | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer latina en el mercado USA. Modelo y creadora de contenido bilingüe para marcas que buscan llegar al mercado hispano en Estados Unidos. @${BRAND_HANDLE}`,
      },
      miami: {
        title: `Influencer Miami | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer latina con conexión a Miami. Modelo y creadora de contenido bilingüe de lifestyle, fitness y belleza para el mercado hispano en Florida. @${BRAND_HANDLE}`,
      },
      newYork: {
        title: `Influencer New York | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer latina conectada con Nueva York. Modelo y creadora de contenido bilingüe de lifestyle, moda y belleza. @${BRAND_HANDLE}`,
      },
      losAngeles: {
        title: `Influencer Los Angeles | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer latina con presencia en Los Ángeles. Modelo y creadora de contenido bilingüe de fitness, lifestyle y belleza para el mercado hispano en California. @${BRAND_HANDLE}`,
      },
      hispanicInfluencer: {
        title: `Hispanic Influencer | ${BRAND_PERSON_NAME} — Creadora de Contenido Bilingüe`,
        description: `${BRAND_PERSON_NAME}, influencer hispana argentina bilingüe. Modelo y creadora de lifestyle, fitness y belleza con alcance en el mercado hispano de USA y Latinoamérica. @${BRAND_HANDLE}`,
      },
      latinInfluencer: {
        title: `Latin Influencer | ${BRAND_PERSON_NAME} — Modelo y Creadora Argentina`,
        description: `${BRAND_PERSON_NAME}, influencer latina argentina. Modelo y creadora de contenido de lifestyle, fitness y belleza con audiencia en Latinoamérica, España y USA. @${BRAND_HANDLE}`,
      },
      modellaArgentina: {
        title: `Modella Argentina | ${BRAND_PERSON_NAME} — Content Creator Buenos Aires`,
        description: `${BRAND_PERSON_NAME}, modella e content creator argentina di Buenos Aires. Contenuti di lifestyle, fitness e bellezza per un pubblico internazionale. @${BRAND_HANDLE}`,
      },
      italia: {
        title: `Influencer Argentina in Italia | ${BRAND_PERSON_NAME} — Modella e Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer e modella argentina con pubblico internazionale. Contenuti di lifestyle, fitness e bellezza per il mercato italiano. @${BRAND_HANDLE}`,
      },
      london: {
        title: `London Influencer | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer latina argentina con audiencia en Londres y el Reino Unido. Modelo y creadora de contenido bilingüe de lifestyle, moda y belleza. @${BRAND_HANDLE}`,
      },
    },
  },
  en: {
    common: {
      siteName: BRAND_SITE_NAME,
      tagline: "Model & social content creator",
      description:
        `${BRAND_PERSON_NAME} — model and social media content creator, ${BRAND_AGE}, based in Buenos Aires (CABA). Campaigns, collaborations and community.`,
      keywords:
        `${BRAND_PERSON_NAME}, model, content creator, social media, Buenos Aires, CABA, influencer, ${BRAND_AGE}, Instagram, TikTok`,
      ogDescription: `Model · Social content · ${BRAND_AGE} · Buenos Aires (CABA)`,
    },
    pages: {
      index: {
        title: `${BRAND_PERSON_NAME} | Model & social content creator — Buenos Aires (CABA)`,
        description:
          `${BRAND_PERSON_NAME} — model and social media content creator, ${BRAND_AGE}, from Buenos Aires (CABA). Campaigns, collaborations and community.`,
      },
      contentCreator: {
        title: `${BRAND_PERSON_NAME} - Content Creator | Lifestyle & Beauty Digital Creator`,
        description: `${BRAND_PERSON_NAME} is a young content creator, model, athlete and nutrition student. She loves lifestyle and shares authentic content on Instagram, TikTok and YouTube. Follow @${BRAND_HANDLE}.`,
      },
      creadoraContenido: {
        title: `${BRAND_PERSON_NAME} - Digital Content Creator | Lifestyle & Beauty`,
        description: `${BRAND_PERSON_NAME} is a young content creator, model, athlete and nutrition student. She loves lifestyle and shares authentic content on Instagram, TikTok and YouTube.`,
      },
      influencer: {
        title: `${BRAND_PERSON_NAME} - Argentina Influencer | Lifestyle, Beauty & Travel`,
        description: `${BRAND_PERSON_NAME} is a young content creator, model, athlete and nutrition student. She loves lifestyle and shares authentic content about wellness, beauty and healthy living.`,
      },
      fotos: {
        title: `${BRAND_PERSON_NAME} Photos | Photo Gallery & Images`,
        description: `${BRAND_PERSON_NAME} photo gallery. Discover exclusive images on Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      imagenes: {
        title: `${BRAND_PERSON_NAME} Images | Photos & Gallery`,
        description: `${BRAND_PERSON_NAME} images and photos. Complete visual content gallery on Instagram, TikTok and YouTube.`,
      },
      reels: {
        title: `${BRAND_PERSON_NAME} Reels | Short Videos on Instagram & TikTok`,
        description: `${BRAND_PERSON_NAME} reels and short videos. Viral content on Instagram and TikTok. Follow @${BRAND_HANDLE} for more reels.`,
      },
      videos: {
        title: `${BRAND_PERSON_NAME} Videos | Content on YouTube, TikTok and Instagram`,
        description: `${BRAND_PERSON_NAME} videos on YouTube, TikTok and Instagram. Exclusive and entertaining content. @${BRAND_HANDLE}`,
      },
      video: {
        title: `${BRAND_PERSON_NAME} Video | Exclusive Content`,
        description: `Exclusive ${BRAND_PERSON_NAME} video. Content on YouTube, TikTok and Instagram. @${BRAND_HANDLE}`,
      },
      lifestyle: {
        title: `Lifestyle with ${BRAND_PERSON_NAME} | Tips & Inspiration`,
        description: `Discover lifestyle content with ${BRAND_PERSON_NAME}. Daily routines, wellness tips, fashion and inspiring everyday moments.`,
      },
      nutricion: {
        title: `Nutrition & Wellness with ${BRAND_PERSON_NAME} | Healthy Tips`,
        description: `${BRAND_PERSON_NAME} is a nutrition student and shares nutrition and wellness tips. Healthy recipes, nutrition tips and healthy lifestyle based on her academic training.`,
      },
      belleza: {
        title: `Beauty & Beauty Tips with ${BRAND_PERSON_NAME} | Beauty Advice`,
        description: `Beauty tips and routines with ${BRAND_PERSON_NAME}. Makeup tips, skincare and personal care.`,
      },
      viajes: {
        title: `Travel with ${BRAND_PERSON_NAME} | Destinations & Inspiration`,
        description: `Discover ${BRAND_PERSON_NAME}'s travels. Destinations, experiences and travel recommendations.`,
      },
      tiktok: {
        title: `${BRAND_PERSON_NAME} on TikTok | Viral Videos & Trending Content`,
        description: `Follow ${BRAND_PERSON_NAME} on TikTok. Viral videos, trending content and entertainment. @${BRAND_HANDLE}`,
      },
      instagram: {
        title: `${BRAND_PERSON_NAME} Instagram | Reels, Stories & Daily Content`,
        description: `Follow ${BRAND_PERSON_NAME} on Instagram. Reels, stories and daily content. @${BRAND_HANDLE}`,
      },
      youtube: {
        title: `${BRAND_PERSON_NAME} YouTube | Videos, Vlogs & Exclusive Content`,
        description: `${BRAND_PERSON_NAME} YouTube channel. Videos, vlogs and exclusive content. Subscribe to @${BRAND_HANDLE}`,
      },
      erome: {
        title: `${BRAND_PERSON_NAME} - Erome | Videos & Exclusive Content`,
        description: `${BRAND_PERSON_NAME} on Erome. Videos and exclusive content.`,
      },
      filtrados: {
        title: `${BRAND_PERSON_NAME} - Official Content | Instagram, TikTok and YouTube`,
        description: `Official ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      filtro: {
        title: `${BRAND_PERSON_NAME} - Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      leaked: {
        title: `${BRAND_PERSON_NAME} - Authentic Content | Instagram, TikTok and YouTube`,
        description: `Authentic ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      onlyfans: {
        title: `${BRAND_PERSON_NAME} - Onlyfans | Official Content`,
        description: `${BRAND_PERSON_NAME} on Onlyfans. Official and exclusive content.`,
      },
      pack: {
        title: `${BRAND_PERSON_NAME} - Digital Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} digital content. Find official videos, photos and reels on Instagram, TikTok and YouTube @${BRAND_HANDLE}.`,
      },
      desnuda: {
        title: `${BRAND_PERSON_NAME} - Lifestyle Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} lifestyle content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      nude: {
        title: `${BRAND_PERSON_NAME} - Official Content | Instagram, TikTok and YouTube`,
        description: `Official ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      premium: {
        title: `${BRAND_PERSON_NAME} - Premium Content | Instagram, TikTok and YouTube`,
        description: `Premium ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      premiumInvite: {
        title: `VIP membership | ${BRAND_PERSON_NAME} — private chat & exclusive content`,
        description: `Subscribe to VIP with secure checkout and quick activation. Telegram-only option also available. @${BRAND_HANDLE}`,
      },
      premiumInviteSuccess: {
        title: `Thank you! | ${BRAND_PERSON_NAME}`,
        description: `Your payment was processed. Follow the steps to access ${BRAND_PERSON_NAME}'s community and benefits. @${BRAND_HANDLE}`,
      },
      privado: {
        title: `${BRAND_PERSON_NAME} - Private Content | Instagram, TikTok and YouTube`,
        description: `Private ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      contenido: {
        title: `${BRAND_PERSON_NAME} - Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      exclusivo: {
        title: `${BRAND_PERSON_NAME} - Exclusive Content | Instagram, TikTok and YouTube`,
        description: `Exclusive ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      gratis: {
        title: `${BRAND_PERSON_NAME} - Free Content | Instagram, TikTok and YouTube`,
        description: `Free ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      galeria: {
        title: `${BRAND_PERSON_NAME} - Gallery | Photos and Videos`,
        description: `${BRAND_PERSON_NAME} gallery. Exclusive photos and videos. @${BRAND_HANDLE}`,
      },
      portfolio: {
        title: `${BRAND_PERSON_NAME} - Portfolio | Fashion Productions`,
        description: `${BRAND_PERSON_NAME} portfolio. Fashion and editorial productions. @${BRAND_HANDLE}`,
      },
      mediakit: {
        title: `${BRAND_PERSON_NAME} - Media Kit | Brand Collaborations`,
        description: `Commercial media kit for ${BRAND_PERSON_NAME}. Metrics, audience and campaign concepts. @${BRAND_HANDLE}`,
      },
      fotosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Photos | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} photos. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      videosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} videos. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      pornhub: {
        title: `${BRAND_PERSON_NAME} - Official Content | Instagram, TikTok and YouTube`,
        description: `Official ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      xvideos: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} videos. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      fotosGratis: {
        title: `Free ${BRAND_PERSON_NAME} Photos | Free Gallery`,
        description: `Free ${BRAND_PERSON_NAME} photos. Free image gallery. @${BRAND_HANDLE}`,
      },
      videosGratis: {
        title: `Free ${BRAND_PERSON_NAME} Videos | Free Content`,
        description: `Free ${BRAND_PERSON_NAME} videos. Free content. @${BRAND_HANDLE}`,
      },
      tiktoker: {
        title: `${BRAND_PERSON_NAME} TikToker | Viral Content Creator Argentina`,
        description: `${BRAND_PERSON_NAME} Argentine TikToker. Viral content, trends and entertainment. Follow @${BRAND_HANDLE} for daily videos and challenges.`,
      },
      modelo: {
        title: `${BRAND_PERSON_NAME} Model | Argentine Instagram and Social Media Model`,
        description: `${BRAND_PERSON_NAME} professional Argentine model. Photo shoots, advertising campaigns and fashion content. @${BRAND_HANDLE}`,
      },
      photography: {
        title: `${BRAND_PERSON_NAME} Photography | Visual and Photographic Content`,
        description: `${BRAND_PERSON_NAME} photography. Professional visual content, photo shoots and digital art. @${BRAND_HANDLE}`,
      },
      artista: {
        title: `${BRAND_PERSON_NAME} Artist | Digital Creative and Visual Artist`,
        description: `${BRAND_PERSON_NAME} digital artist. Creative content, visual art and artistic expression through social media. @${BRAND_HANDLE}`,
      },
      fashion: {
        title: `${BRAND_PERSON_NAME} Fashion | Style, Fashion & Trends`,
        description: `${BRAND_PERSON_NAME} fashion and style. Trends, outfits, looks and fashion advice. Daily fashion inspiration. @${BRAND_HANDLE}`,
      },
      wellness: {
        title: `${BRAND_PERSON_NAME} Wellness | Wellbeing, Health & Healthy Living`,
        description: `${BRAND_PERSON_NAME} wellness and wellbeing. Health tips, healthy living, mindfulness and self-care. @${BRAND_HANDLE}`,
      },
      digitalCreator: {
        title: `${BRAND_PERSON_NAME} Digital Creator | Digital Content Creator`,
        description: `${BRAND_PERSON_NAME} digital creator. Multi-platform content on Instagram, TikTok and YouTube. Digital innovation and creativity. @${BRAND_HANDLE}`,
      },
      socialMedia: {
        title: `${BRAND_PERSON_NAME} Social Media | Social Media & Multi-Platform Content`,
        description: `${BRAND_PERSON_NAME} on social media. Multi-platform content, digital strategy and community building. @${BRAND_HANDLE}`,
      },
      personalidad: {
        title: `${BRAND_PERSON_NAME} Personality | Argentine Digital Personality`,
        description: `${BRAND_PERSON_NAME} digital personality. Authentic content, personal experiences and genuine connection with audience. @${BRAND_HANDLE}`,
      },
      beautyExpert: {
        title: `${BRAND_PERSON_NAME} Beauty Expert | Beauty and Makeup Expert`,
        description: `${BRAND_PERSON_NAME} beauty expert. Makeup tips, skincare, beauty routines and personal care advice. @${BRAND_HANDLE}`,
      },
      travelInfluencer: {
        title: `${BRAND_PERSON_NAME} Travel Influencer | Travel & Adventures`,
        description: `${BRAND_PERSON_NAME} travel influencer. Travel adventures, unique destinations, cultural experiences and exploration. @${BRAND_HANDLE}`,
      },
      creative: {
        title: `${BRAND_PERSON_NAME} Creative | Creative and Artistic Content`,
        description: `${BRAND_PERSON_NAME} creative content. Digital art, conceptual photography, creative projects and artistic expression. @${BRAND_HANDLE}`,
      },
      argentina: {
        title: `${BRAND_PERSON_NAME} Argentina | Model & social content creator`,
        description:
          `${BRAND_PERSON_NAME} — model and social content creator, ${BRAND_AGE}, Buenos Aires (CABA). @${BRAND_HANDLE}`,
      },
      buenosAires: {
        title: `${BRAND_PERSON_NAME} Buenos Aires | Model & social content creator`,
        description:
          `${BRAND_PERSON_NAME} from Buenos Aires (CABA). Model and social media content creator. @${BRAND_HANDLE}`,
      },
      deportista: {
        title: `${BRAND_PERSON_NAME} Athlete | Fitness Content`,
        description: `${BRAND_PERSON_NAME} athlete. Workout routines, sports nutrition and physical wellness tips. Athlete content. @${BRAND_HANDLE}`,
      },
      fitness: {
        title: `${BRAND_PERSON_NAME} Fitness | Workouts and Training Tips`,
        description: `${BRAND_PERSON_NAME} fitness. Workouts, exercise routines and motivation. Combines training with nutrition as an athlete. @${BRAND_HANDLE}`,
      },
      entrenamiento: {
        title: `${BRAND_PERSON_NAME} Training | Workout Routines`,
        description: `${BRAND_PERSON_NAME} training routines. Effective exercises, technique tips and recovery. Sports content. @${BRAND_HANDLE}`,
      },
      atleta: {
        title: `${BRAND_PERSON_NAME} Athlete | Sports Content`,
        description: `${BRAND_PERSON_NAME} athlete. Discipline, training and self-improvement. Authentic sports content. @${BRAND_HANDLE}`,
      },
      sobreMi: {
        title: `About ${BRAND_PERSON_NAME} | Model & social content creator`,
        description:
          `Meet ${BRAND_PERSON_NAME} — model and social content creator, ${BRAND_AGE}, Buenos Aires (CABA). @${BRAND_HANDLE}`,
      },
      biografia: {
        title: `${BRAND_PERSON_NAME} Biography | Argentina Content Creator`,
        description: `${BRAND_PERSON_NAME} biography. Argentine content creator on Instagram, TikTok and YouTube. Lifestyle, fitness and beauty. @${BRAND_HANDLE}`,
      },
      rutina: {
        title: `${BRAND_PERSON_NAME} Routine | Daily Routine and Tips`,
        description: `${BRAND_PERSON_NAME} daily routine. Productive mornings, workout, skincare and balance. Lifestyle tips. @${BRAND_HANDLE}`,
      },
      recetas: {
        title: `${BRAND_PERSON_NAME} Recipes | Healthy Recipes`,
        description: `${BRAND_PERSON_NAME} healthy recipes. As a nutrition student she shares smoothies, balanced meals and snacks. @${BRAND_HANDLE}`,
      },
      skincare: {
        title: `${BRAND_PERSON_NAME} Skincare | Skin Care and Beauty`,
        description: `${BRAND_PERSON_NAME} skincare. Facial routines, favorite products and skin care tips. Beauty and wellness. @${BRAND_HANDLE}`,
      },
      colombia: {
        title: `Influencer Colombia | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, model and content creator with a community in Colombia. Lifestyle, fitness and beauty content from Buenos Aires for all of Latin America. @${BRAND_HANDLE}`,
      },
      medellin: {
        title: `Influencer Medellín | ${BRAND_PERSON_NAME} — Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer with an audience in Medellín and Colombia. Model and content creator specializing in lifestyle, fitness and beauty. @${BRAND_HANDLE}`,
      },
      bogota: {
        title: `Influencer Bogotá | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer with followers in Bogotá and all of Colombia. Authentic lifestyle, beauty and fitness content. @${BRAND_HANDLE}`,
      },
      mexico: {
        title: `Influencer México | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, model and content creator with a community in Mexico. Lifestyle, beauty and fitness with a Latin American style. @${BRAND_HANDLE}`,
      },
      guadalajara: {
        title: `Influencer Guadalajara | ${BRAND_PERSON_NAME} — Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer with an audience in Guadalajara and across Mexico. Authentic lifestyle, fitness and beauty content. @${BRAND_HANDLE}`,
      },
      ciudadDeMexico: {
        title: `Influencer CDMX | ${BRAND_PERSON_NAME} — Mexico City`,
        description: `${BRAND_PERSON_NAME}, influencer with followers in Mexico City (CDMX). Latin American model and content creator specializing in lifestyle and beauty. @${BRAND_HANDLE}`,
      },
      chile: {
        title: `Influencer Chile | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, model and content creator with a community in Chile. Lifestyle, fitness and beauty content from Buenos Aires for all of Latin America. @${BRAND_HANDLE}`,
      },
      santiago: {
        title: `Influencer Santiago | ${BRAND_PERSON_NAME} — Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer with an audience in Santiago de Chile. Model and content creator specializing in lifestyle, fitness and beauty. @${BRAND_HANDLE}`,
      },
      espana: {
        title: `Influencer Spain | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, model and content creator with followers in Spain. Lifestyle, beauty and fitness with a Latin American style. @${BRAND_HANDLE}`,
      },
      madrid: {
        title: `Influencer Madrid | ${BRAND_PERSON_NAME} — Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer with a community in Madrid. Model and content creator specializing in lifestyle, beauty and fitness. @${BRAND_HANDLE}`,
      },
      barcelona: {
        title: `Influencer Barcelona | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, influencer with an audience in Barcelona. Authentic lifestyle, fashion and beauty content for the Spanish-speaking community. @${BRAND_HANDLE}`,
      },
      contacto: {
        title: `Contact | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `Get in touch with ${BRAND_PERSON_NAME}. Collaborations, campaigns and brand partnerships. @${BRAND_HANDLE}`,
      },
      latinaInfluencer: {
        title: `Latina Influencer | ${BRAND_PERSON_NAME} — Bilingual Content Creator`,
        description: `${BRAND_PERSON_NAME}, Argentine Latina influencer and bilingual content creator (ES/EN). Lifestyle, fitness and beauty reaching US and Latin American audiences. @${BRAND_HANDLE}`,
      },
      usa: {
        title: `Influencer USA | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, Latina influencer in the US market. Bilingual model and content creator for brands targeting the Hispanic market in the United States. @${BRAND_HANDLE}`,
      },
      miami: {
        title: `Miami Influencer | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, Latina influencer connected to Miami. Bilingual lifestyle, fitness and beauty content creator for the Hispanic Florida market. @${BRAND_HANDLE}`,
      },
      newYork: {
        title: `New York Influencer | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, Latina influencer connected to New York. Bilingual lifestyle, fashion and beauty content creator. @${BRAND_HANDLE}`,
      },
      losAngeles: {
        title: `Los Angeles Influencer | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, Latina influencer in Los Angeles. Bilingual fitness, lifestyle and beauty content creator for the Hispanic California market. @${BRAND_HANDLE}`,
      },
      hispanicInfluencer: {
        title: `Hispanic Influencer | ${BRAND_PERSON_NAME} — Bilingual Content Creator`,
        description: `${BRAND_PERSON_NAME}, Argentine Hispanic influencer and bilingual content creator. Lifestyle, fitness and beauty content reaching the US Hispanic market and Latin America. @${BRAND_HANDLE}`,
      },
      latinInfluencer: {
        title: `Latin Influencer | ${BRAND_PERSON_NAME} — Argentine Model & Creator`,
        description: `${BRAND_PERSON_NAME}, Argentine Latin influencer and model. Lifestyle, fitness and beauty content creator with audiences across Latin America, Spain and the USA. @${BRAND_HANDLE}`,
      },
      modellaArgentina: {
        title: `Argentine Model | ${BRAND_PERSON_NAME} — Content Creator Buenos Aires`,
        description: `${BRAND_PERSON_NAME}, Argentine model and content creator from Buenos Aires. Lifestyle, fitness and beauty content for an international audience. @${BRAND_HANDLE}`,
      },
      italia: {
        title: `Argentine Influencer in Italy | ${BRAND_PERSON_NAME} — Model & Content Creator`,
        description: `${BRAND_PERSON_NAME}, Argentine influencer and model with growing international audience. Lifestyle, fitness and beauty content for the Italian market. @${BRAND_HANDLE}`,
      },
      london: {
        title: `London Influencer | ${BRAND_PERSON_NAME} — Latina Content Creator`,
        description: `${BRAND_PERSON_NAME}, Argentine Latina influencer with audience in London and the UK. Bilingual lifestyle, fashion and beauty content creator. @${BRAND_HANDLE}`,
      },
    },
  },
};

export const getTranslation = (lang: Language | undefined, path: string): string => {
  const keys = path.split(".");
  // Extract base language code for translations (e.g., "es-AR" -> "es")
  const baseLang = getBaseLanguage(lang);
  let value: any = translations[baseLang];
  
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      // Fallback to Spanish if translation not found
      value = translations.es;
      for (const k of keys) {
        value = value?.[k];
      }
      break;
    }
  }
  
  return value || "";
};
